import { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import { auth, googleProvider } from "../lib/firebase";

const AuthContext = createContext(null);
const ADMIN_SESSION_KEY = "cns_admin_session";
const ADMIN_EMAIL_KEY = "cns_admin_email";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginOpen, setLoginOpen] = useState(false);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(
    () => localStorage.getItem(ADMIN_SESSION_KEY) === "1"
  );
  const [adminEmail, setAdminEmail] = useState(() => localStorage.getItem(ADMIN_EMAIL_KEY) || "");

  const mapUser = useCallback(async (firebaseUser) => {
    if (!firebaseUser) return null;
    const tokenResult = await firebaseUser.getIdTokenResult();
    const isAdmin = Boolean(tokenResult?.claims?.admin || tokenResult?.claims?.role === "admin");
    return {
      id: firebaseUser.uid,
      name: firebaseUser.displayName || firebaseUser.email || "Member",
      email: firebaseUser.email || "",
      picture: firebaseUser.photoURL || "",
      role: isAdmin ? "admin" : "user",
    };
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        setUser(await mapUser(firebaseUser));
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    });

    return () => {
      unsub();
    };
  }, [mapUser]);

  const signInWithGoogle = async () => {
    const result = await signInWithPopup(auth, googleProvider);
    const nextUser = await mapUser(result.user);
    setUser(nextUser);
    setLoginOpen(false);
    const intent = sessionStorage.getItem("cns_login_intent");
    sessionStorage.removeItem("cns_login_intent");
    if (intent === "cart") {
      window.location.assign("/cart");
      return;
    }
    if (intent === "custom") {
      window.location.assign("/custom");
      return;
    }
    if (intent === "account") {
      window.location.assign("/account");
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch {}
    setUser(null);
  };

  const loginAdmin = async (email, password) => {
    const configuredEmail = (process.env.REACT_APP_ADMIN_EMAIL || "").trim().toLowerCase();
    const configuredPassword = process.env.REACT_APP_ADMIN_PASSWORD || "";
    const normalizedEmail = (email || "").trim().toLowerCase();
    if (!configuredEmail || !configuredPassword) {
      throw new Error("Admin credentials are not configured");
    }
    if (normalizedEmail !== configuredEmail || password !== configuredPassword) {
      throw new Error("Invalid admin credentials");
    }
    localStorage.setItem(ADMIN_SESSION_KEY, "1");
    localStorage.setItem(ADMIN_EMAIL_KEY, normalizedEmail);
    setIsAdminAuthenticated(true);
    setAdminEmail(normalizedEmail);
  };

  const logoutAdmin = () => {
    localStorage.removeItem(ADMIN_SESSION_KEY);
    localStorage.removeItem(ADMIN_EMAIL_KEY);
    setIsAdminAuthenticated(false);
    setAdminEmail("");
  };

  const refresh = useCallback(async () => {
    try {
      if (auth.currentUser) {
        await auth.currentUser.reload();
      }
    } catch {}
    try {
      setUser(await mapUser(auth.currentUser));
    } catch {
      setUser(null);
    }
  }, [mapUser]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        loginOpen,
        setLoginOpen,
        logout,
        refresh,
        signInWithGoogle,
        isAdminAuthenticated,
        adminEmail,
        loginAdmin,
        logoutAdmin,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
